/**
 * @friskai/frisk-js SDK Types
 *
 * Public type definitions for the Frisk SDK.
 */

import { FrameworkAdapter } from '../framework-adapter'
import type { FriskSDKLoggingOptions } from '../logging'
import type { FriskExtension } from './extensions'
import type { RedactionOptions } from './redaction-options'

/**
 * Decision made by the policy engine for a tool call
 */
export enum DecisionOutcome {
  ALLOW = 'allow',
  DENY = 'deny',
  ERROR = 'error',
  ESCALATE = 'escalate',
}

/**
 * Result of a policy evaluation for a tool call
 */
export interface ToolCallDecision {
  /**
   * The decision made by the policy engine
   */
  outcome: DecisionOutcome

  /**
   * Number of policy rules that matched this tool call
   */
  rulesMatchedCount: number

  /**
   * Human-readable reason for the decision
   */
  reason: string

  /**
   * Policy identifier associated with an escalated decision, when available.
   */
  policyId?: string

  /**
   * Policy version identifier associated with an escalated decision, when available.
   */
  policyVersionId?: string
}

/**
 * Input parameters for Frisk initialization
 */
export interface FriskOptions<
  ToolCallT = unknown,
  ToolT = unknown,
  FrameworkAdapterT extends FrameworkAdapter<
    ToolCallT,
    ToolT
  > = FrameworkAdapter<ToolCallT, ToolT>,
> {
  /**
   * API key for authentication with Frisk services.
   * If not provided, will attempt to read from FRISK_API_KEY environment variable.
   */
  apiKey?: string

  /**
   * Base URL for the Frisk API.
   * Defaults to FRISK_BASE_URL env var or 'https://api.frisk.ai'
   */
  apiBaseUrl?: string

  /**
   * Framework adapter used to integrate Frisk with a specific agent framework (e.g. LangChain).
   * If not provided, Frisk uses the default adapter.
   */
  adapter?: FrameworkAdapterT

  /**
   * OpenTelemetry collector endpoint for telemetry.
   * Defaults to FRISK_TELEMETRY_ENDPOINT env var or 'https://otel-collector.docker.localhost:443'
   */
  otlpEndpoint?: string

  /**
   * SDK options for redaction configuration
   */
  redact?: RedactionOptions

  /**
   * Logging options for the SDK, including custom logger and log level.
   */
  logging?: FriskSDKLoggingOptions

  /**
   * Extensions to register with the Frisk instance (e.g. ToolPolicies).
   */
  extensions?: FriskExtension[]
}

/** Raw tool-call shape accepted by generic Frisk evaluation APIs. */
export interface ToolCall {
  /** Optional framework-provided tool call ID. */
  id?: string

  /** Tool name used for policy matching and execution routing. */
  name: string

  /** Tool input arguments. */
  args: Record<string, unknown>
}

/**
 * Information about a tool call
 */
export interface NormalizedToolCall {
  /**
   * Unique identifier for this tool call
   */
  id: string

  /**
   * Name of the tool being called
   */
  name: string

  /**
   * Arguments passed to the tool
   */
  args: Record<string, unknown>
}

/**
 * OpenTelemetry trace context carrier for distributed tracing
 */
export interface TraceContextCarrier {
  [key: string]: string
}

/**
 * Result of a redaction operation
 */
export interface RedactionResult {
  /**
   * The redacted data as a JSON string
   */
  redacted: string

  /**
   * Array of field paths that were redacted
   */
  matchedPaths: string[]
}
